/**
 * PROP SWAPS — runtime (2026-11-04, owner: "map props more realistic").
 *
 * Replaces small groups of map boxes (water tower, wagons, barrels, wells,
 * haystacks, cover crates) with real, restyled models — layout rules and the
 * placement contract are in propSwapLayout.ts; per-map rules live in
 * mapExtras/<mapId>.ts as an optional `propSwaps(map)` export.
 *
 * Same load contract as the scanned rocks (mapProps.ts):
 *  - `planPropSwaps` runs while the map is built. When real models are on
 *    (every machine but the TV app, gltfAssets realModelsEnabled) it starts
 *    the downloads and tells the box builder which boxes to DIVERT: they are
 *    still drawn, by the same code path and materials, but into their own
 *    per-kind fallback meshes (`userData.propSwap`). The colliders never
 *    change.
 *  - GameRenderer.precompile() gives the loads a bounded wait under the load
 *    screen and calls `upgradePropSwaps` BEFORE its scene-wide compile: each
 *    loaded kind becomes one InstancedMesh per material (a handful of draw
 *    calls for the whole map) and its fallback meshes are dropped. A kind that
 *    did not load in time keeps its box look for the round — never a pop-in.
 */
import * as THREE from "three";
import type { MapBox, MapDef } from "@neon-purge/shared";
import { loadGltf, MODEL_LOAD_WAIT_MS } from "./gltfAssets.js";
import { genericSwaps, placeSwap, PROP_KINDS, type PropKind, type PropPlacement, type PropSwap } from "./propSwapLayout.js";

const BASE = "/models/props";

interface RuleModule { propSwaps?: (map: MapDef) => PropSwap[] }
let ruleMods: Record<string, RuleModule> = {};
try {
  ruleMods = import.meta.glob<RuleModule>("./mapExtras/*.ts", { eager: true });
} catch { ruleMods = {}; }

/** Every swap for a map: per-map rules first, then the generic crates. */
export function collectPropSwaps(map: MapDef): PropSwap[] {
  const out: PropSwap[] = [];
  const used = new Set<MapBox>();
  const add = (list: PropSwap[]): void => {
    for (const sw of list) {
      if (sw.boxes.some((b) => used.has(b))) continue;
      for (const b of sw.boxes) used.add(b);
      out.push(sw);
    }
  };
  for (const [path, m] of Object.entries(ruleMods)) {
    if (typeof m?.propSwaps !== "function") continue;
    try { add(m.propSwaps(map)); } catch (e) { console.error(`[prop swaps] ${path} failed`, e); }
  }
  add(genericSwaps(map));
  return out;
}

interface Plan { placements: Map<PropKind, PropPlacement[]>; loads: Promise<boolean> | null }
let plan: Plan = { placements: new Map(), loads: null };

/**
 * Plan the swaps for the map being built. Returns box → kind for every box the
 * builder must divert into a fallback mesh (empty when real models are off).
 */
export function planPropSwaps(map: MapDef, enabled: boolean): Map<MapBox, PropKind> {
  plan = { placements: new Map(), loads: null };
  const divert = new Map<MapBox, PropKind>();
  if (!enabled) return divert;
  for (const sw of collectPropSwaps(map)) {
    const p = placeSwap(sw);
    if (!p) continue;
    (plan.placements.get(sw.kind) ?? plan.placements.set(sw.kind, []).get(sw.kind)!).push(p);
    for (const b of sw.replace) divert.set(b, sw.kind);
  }
  const kinds = [...plan.placements.keys()];
  if (kinds.length) {
    plan.loads = Promise.all(kinds.map((k) => loadGltf(`${BASE}/${PROP_KINDS[k].file}`)))
      .then((gs) => gs.every(Boolean));
  }
  return divert;
}

/** Start downloading the prop models `map` will swap in, WITHOUT touching the
 *  current plan (matchPrefetch.ts — the Play screen knows the map long before
 *  the match builds it). Same cached loadGltf promises planPropSwaps awaits. */
export function prefetchPropSwapModels(map: MapDef): void {
  const kinds = new Set<PropKind>();
  for (const sw of collectPropSwaps(map)) if (placeSwap(sw)) kinds.add(sw.kind);
  for (const k of kinds) void loadGltf(`${BASE}/${PROP_KINDS[k].file}`);
}

/** Bounded wait (precompile, under the load screen). */
export async function propSwapsReady(maxMs = MODEL_LOAD_WAIT_MS): Promise<boolean> {
  if (!plan.loads) return false;
  return Promise.race([plan.loads, new Promise<boolean>((r) => window.setTimeout(() => r(false), maxMs))]);
}

/** Swap every loaded kind in. Returns how many kinds were upgraded. Call only
 *  under the load screen. Idempotent: a kind's fallback meshes are gone after. */
export async function upgradePropSwaps(root: THREE.Object3D, shadows: boolean): Promise<number> {
  const fallbacks = new Map<string, THREE.Mesh[]>();
  root.traverse((o) => {
    const k = o.userData?.propSwap as string | undefined;
    if (k && (o as THREE.Mesh).isMesh) (fallbacks.get(k) ?? fallbacks.set(k, []).get(k)!).push(o as THREE.Mesh);
  });
  let n = 0;
  const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), pos = new THREE.Vector3(), scl = new THREE.Vector3();
  const up = new THREE.Vector3(0, 1, 0);
  for (const [kind, list] of plan.placements) {
    const olds = fallbacks.get(kind);
    if (!olds?.length) continue;
    const g = await loadGltf(`${BASE}/${PROP_KINDS[kind].file}`); // cached: resolves at once
    if (!g) continue;
    const parent = olds[0].parent ?? root;
    g.scene.updateMatrixWorld(true);
    const parts: THREE.Mesh[] = [];
    g.scene.traverse((o) => { if ((o as THREE.Mesh).isMesh) parts.push(o as THREE.Mesh); });
    for (const part of parts) {
      // The geometry is the cached, SHARED one (userData.shared → never
      // disposed by the map walker) and meshopt-QUANTIZED: positions are
      // normalized int16 whose dequantization lives in the node matrix. Baking
      // that matrix into the attribute would clamp it to [-1, 1], so it is
      // folded into every instance matrix instead: placement × node matrix.
      const geo = part.geometry;
      // per-map material clone (the map's dispose walker frees materials; the
      // textures stay shared/cached)
      const mat = (part.material as THREE.Material).clone();
      const im = new THREE.InstancedMesh(geo, mat, list.length);
      list.forEach((p, i) => {
        q.setFromAxisAngle(up, p.yaw);
        m4.compose(pos.set(p.x, p.y, p.z), q, scl.set(p.sx, p.sy, p.sz));
        m4.multiply(part.matrixWorld);
        im.setMatrixAt(i, m4);
      });
      im.instanceMatrix.needsUpdate = true;
      im.castShadow = shadows;
      im.receiveShadow = true;
      im.name = `prop:${kind}:${part.name}`;
      im.computeBoundingSphere();
      // static like the rest of the map (renderer froze its siblings)
      im.matrixAutoUpdate = false;
      im.updateMatrix();
      parent.add(im);
      im.updateMatrixWorld(true);
      im.matrixWorldAutoUpdate = false;
    }
    for (const o of olds) {
      o.parent?.remove(o);
      o.geometry.dispose(); // materials are the map's shared family materials
    }
    n++;
  }
  return n;
}
